/**
 * Carrier / service rate helpers for label purchase ShipFlow and legacy panel.
 * Aligns carrier slugs (ups), display names (UPS), and EasyPost rate rows.
 */

export type LabelRateRow = {
  id?: string
  carrier?: string
  service?: string
  rate?: number
  shipment_id?: string
  [key: string]: unknown
}

export type ServiceSelectOption = {
  value: string
  label: string
  title?: string
}

export type LabelRatesFetchPhase = 'idle' | 'loading' | 'success' | 'error' | 'empty'

export function normalizeCarrierName(value: string | null | undefined): string {
  const v = String(value ?? '').trim().toLowerCase()
  if (!v || v === 'none' || v === 'pick up') return ''
  if (v === 'ups' || v.includes('united parcel')) return 'ups'
  if (v === 'usps' || v.includes('postal service')) return 'usps'
  if (v === 'fedex' || v.includes('fed ex')) return 'fedex'
  if (v === 'dhl') return 'dhl'
  if (v === 'asendia') return 'asendia'
  return v.replace(/\s+/g, '')
}

export function carriersMatch(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const na = normalizeCarrierName(a)
  const nb = normalizeCarrierName(b)
  return !!na && !!nb && na === nb
}

export function isCarrierSelected(shippingMethod: string | null | undefined): boolean {
  return !!normalizeCarrierName(shippingMethod)
}

/** Display label for carrier slug (UPS, USPS, …). */
export function carrierDisplayName(shippingMethod: string | null | undefined): string {
  const slug = normalizeCarrierName(shippingMethod)
  const map: Record<string, string> = {
    ups: 'UPS',
    usps: 'USPS',
    fedex: 'FedEx',
    dhl: 'DHL',
    asendia: 'Asendia',
  }
  return map[slug] || (shippingMethod || '').trim().toUpperCase() || 'Carrier'
}

export function getRatesForSelectedCarrier(
  rates: LabelRateRow[] | null | undefined,
  selectedCarrier: string | null | undefined,
): LabelRateRow[] {
  if (!Array.isArray(rates) || !rates.length) return []
  if (!isCarrierSelected(selectedCarrier)) return []
  return rates.filter((row) => carriersMatch(row.carrier, selectedCarrier))
}

function serviceValueFromRate(row: LabelRateRow, selectedCarrier: string): string {
  const raw = String(row.service ?? '').trim()
  if (raw.includes(':')) return raw
  const slug = normalizeCarrierName(row.carrier || selectedCarrier)
  const prefix = slug === 'usps' ? 'USPS' : slug === 'ups' ? 'UPS' : slug === 'fedex' ? 'FEDEX' : slug.toUpperCase()
  return `${prefix}:${raw}`
}

export function getServiceOptionsFromRates(
  rates: LabelRateRow[] | null | undefined,
  selectedCarrier: string | null | undefined,
): ServiceSelectOption[] {
  const matched = getRatesForSelectedCarrier(rates, selectedCarrier)
  const seen = new Set<string>()
  const options: ServiceSelectOption[] = []
  for (const row of matched) {
    const serviceName = String(row.service ?? '').trim()
    if (!serviceName) continue
    const value = serviceValueFromRate(row, selectedCarrier || '')
    if (seen.has(value)) continue
    seen.add(value)
    const price =
      row.rate != null && !Number.isNaN(Number(row.rate))
        ? ` — $${(Math.round(Number(row.rate) * 100) / 100).toFixed(2)}`
        : ''
    options.push({
      value,
      label: `${serviceName}${price}`,
      title: serviceName,
    })
  }
  return options
}

export function hasServiceOptionsForSelectedCarrier(
  rates: LabelRateRow[] | null | undefined,
  selectedCarrier: string | null | undefined,
  staticOptionCount = 0,
): boolean {
  return (
    getServiceOptionsFromRates(rates, selectedCarrier).length > 0 || staticOptionCount > 0
  )
}

export function deriveLabelRatesFetchPhase(input: {
  fetching: boolean
  packageComplete: boolean
  rates: LabelRateRow[] | null | undefined
  errorMessage?: string | null
}): LabelRatesFetchPhase {
  if (!input.packageComplete) return 'idle'
  if (input.fetching) return 'loading'
  if (input.errorMessage) return 'error'
  if (!Array.isArray(input.rates)) return 'idle'
  if (input.rates.length === 0) return 'empty'
  return 'success'
}

export type CarrierServiceHelperInput = {
  packageComplete: boolean
  dimensionsComplete: boolean
  weightComplete: boolean
  fetchingLabelRates: boolean
  labelRatesPhase: LabelRatesFetchPhase
  labelRatesError?: string | null
  shippingMethod: string
  serviceOptionCount: number
  ratesReturnedCount: number
  /** True when Redux/service field has a non-empty service name. */
  serviceSelected?: boolean
  /** True when carrier + service match a returned rate row (source of truth for purchase). */
  hasValidSelectedRate?: boolean
}

export const CARRIER_SERVICE_HELPER_COPY = {
  packageIncomplete: 'Enter package dimensions to load carrier rates.',
  weightIncomplete: 'Enter package weight (lb and oz) to load carrier rates.',
  ratesLoading: 'Loading carrier services…',
  selectCarrier: 'Select a carrier to see available services.',
  selectService: 'Select a service to continue.',
  rateError: 'Could not load carrier rates. Check the address, package details, and carrier settings.',
  noRates: 'No rates returned. Check the destination address, package details, or carrier settings.',
  noServicesForCarrier: (name: string) =>
    `No services returned for ${name}. Try another carrier or check carrier settings.`,
} as const

export function getCarrierServiceHelperText(input: CarrierServiceHelperInput): string | null {
  if (!input.packageComplete) {
    if (!input.dimensionsComplete) return CARRIER_SERVICE_HELPER_COPY.packageIncomplete
    if (!input.weightComplete) return CARRIER_SERVICE_HELPER_COPY.weightIncomplete
    return CARRIER_SERVICE_HELPER_COPY.packageIncomplete
  }
  if (input.fetchingLabelRates || input.labelRatesPhase === 'loading') {
    return CARRIER_SERVICE_HELPER_COPY.ratesLoading
  }
  if (input.labelRatesPhase === 'error') {
    return input.labelRatesError || CARRIER_SERVICE_HELPER_COPY.rateError
  }
  if (input.labelRatesPhase === 'empty') {
    return CARRIER_SERVICE_HELPER_COPY.noRates
  }
  if (!isCarrierSelected(input.shippingMethod)) {
    if (input.labelRatesPhase === 'success' && input.ratesReturnedCount > 0) {
      return CARRIER_SERVICE_HELPER_COPY.selectCarrier
    }
    if (input.labelRatesPhase === 'success') return CARRIER_SERVICE_HELPER_COPY.noRates
    return CARRIER_SERVICE_HELPER_COPY.ratesLoading
  }
  if (
    input.labelRatesPhase === 'success' &&
    input.serviceOptionCount === 0
  ) {
    return CARRIER_SERVICE_HELPER_COPY.noServicesForCarrier(
      carrierDisplayName(input.shippingMethod),
    )
  }
  if (input.hasValidSelectedRate || (input.serviceSelected && input.serviceOptionCount > 0)) {
    return null
  }
  if (input.serviceOptionCount > 0) {
    return CARRIER_SERVICE_HELPER_COPY.selectService
  }
  return null
}
